import {FormControl, Label, Separator} from 'platform/components';
import {GridItem, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useFormatNumber} from 'platform/locale';

import {useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNegative, isNumber} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {FUEL_TANK_STEPS} from '../../../../../constants/FUEL_TANK_STEPS';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

interface ConditionInformationRowProps {
  rentalOrder: RentalOrderResponseBody | Nullish;
  control: FormControl<ReturnVehicleFormValues>;
}

export function ConditionInformationRow(props: ConditionInformationRowProps) {
  const mileageWhenReturned = useWatch({control: props.control, name: 'actualMileage'});
  const formatNumber = useFormatNumber();

  const plannedReturnDate = props.rentalOrder?.plannedRentalPeriod?.to;
  const actualMileage = `${formatNumber(props.rentalOrder?.rentVehicle?.actualMileage) ?? EMPTY_PLACEHOLDER} ${i18n.t('general.metric.km')}`;

  const totalKmTraveled =
    (mileageWhenReturned ?? 0) - (props.rentalOrder?.rentVehicle?.actualMileage ?? 0);
  const formattedTotalKmTraveled = isNumber(totalKmTraveled)
    ? `${formatNumber(totalKmTraveled)} ${i18n.t('general.metric.km')}`
    : EMPTY_PLACEHOLDER;

  const foundFuelTankStepLabel = FUEL_TANK_STEPS.find(
    (step) => step.value.toString() === props.rentalOrder?.rentVehicle?.fuelTank
  )?.label;

  const fuelTank =
    foundFuelTankStepLabel ?? props.rentalOrder?.rentVehicle?.fuelTank ?? EMPTY_PLACEHOLDER;

  return (
    <>
      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.plannedReturnDate')}</Label>
        <Text alternative size="small">
          {isNotNil(plannedReturnDate) ? (
            <DateTimeFormat format="dateTimeShort" value={parseDate(plannedReturnDate)} />
          ) : (
            EMPTY_PLACEHOLDER
          )}
        </Text>
      </VStack>

      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.kmStatusWhenRented')}</Label>
        <Text alternative size="small">
          {actualMileage}
        </Text>
      </VStack>

      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.fuelTankWhenRented')}</Label>
        <Text alternative size="small">
          {fuelTank}
        </Text>
      </VStack>

      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.totalKmTraveled')}</Label>
        <Text alternative size="small" color={isNegative(totalKmTraveled) ? 'danger' : undefined}>
          {formattedTotalKmTraveled}
        </Text>
      </VStack>

      <GridItem span={4}>
        <Separator spacing={0} />
      </GridItem>
    </>
  );
}
